import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, PenLine, QrCode, Sparkles, StickyNote, Waypoints, X } from 'lucide-react';
import { JessicaDemoEmbed, JESSICA_PANEL_GRADIENT } from './JessicaDemoEmbed';
import { JessicaMobileHandoffCard } from './JessicaMobileHandoffCard';

const agencySans = "font-[Calibri,'Calibri_Light','Segoe_UI',sans-serif]";

const PROTOTYPE_DISCLAIMER =
  'Public preview: try the mobile assistant, save useful conversation summaries, and explore how memory can carry ideas forward between sessions.';

const CAPABILITIES = [
  {
    icon: MessageCircle,
    title: 'Controlled memory',
    body: 'Save useful parts of a conversation as notes or summaries you can revisit later.',
  },
  {
    icon: Sparkles,
    title: 'Reusable context',
    body: 'Bring saved memories back into future chats so the assistant can build on what already matters.',
  },
  {
    icon: PenLine,
    title: 'Google Drive sync',
    body: 'Keep saved memory portable and organised in your own storage.',
  },
  {
    icon: StickyNote,
    title: 'Shareable outputs',
    body: 'Download, review, and share saved notes and summaries outside the app.',
  },
  {
    icon: Waypoints,
    title: 'Research and ideas',
    body: 'Move quickly from chat to captured ideas without losing the thread.',
  },
];

/**
 * Full-screen Jessica preview — interactive prototype plus capability summary.
 */
export function JessicaExampleModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div className="relative z-[101] flex max-h-[100dvh] min-h-full items-start justify-center overflow-y-auto px-3 py-4 sm:p-6 sm:py-8">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="jessica-example-title"
          className={`portal-modal-panel relative my-auto w-full max-w-[min(100%,1680px)] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl ${agencySans}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="absolute right-3 top-3 z-10 rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>

          <div className="grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)]">
            <div
              className="relative flex min-h-[28rem] w-full items-center justify-center px-6 py-10 sm:px-10 sm:py-12 lg:min-h-[44rem] lg:px-12 lg:py-14"
              style={{ background: JESSICA_PANEL_GRADIENT }}
            >
              <JessicaDemoEmbed variant="modal" className="h-full min-h-[24rem] w-full" />
            </div>

            <div className="min-w-0 border-t border-neutral-200 px-6 py-8 sm:px-8 sm:py-10 lg:border-l lg:border-t-0 lg:px-10 lg:py-12 xl:px-12">
              <h2
                id="jessica-example-title"
                className="text-2xl font-semibold leading-tight text-[#7c3aed] sm:text-[1.75rem]"
              >
                Ouro
              </h2>
              <p className="mt-1 text-sm font-medium text-neutral-500">
                An AI assistant with memory you control
              </p>
              <Link
                to="/ouro"
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#c4b5fd] bg-[#faf5ff] px-4 py-2 text-sm font-semibold text-[#6d28d9] transition-colors hover:bg-[#f3e8ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600]"
              >
                <QrCode className="h-4 w-4" aria-hidden />
                Scan QR for mobile install
              </Link>
              <p
                className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50 px-3.5 py-3 text-sm leading-relaxed text-amber-950"
                role="note"
              >
                {PROTOTYPE_DISCLAIMER}
              </p>
              <p className="mt-5 text-base leading-relaxed text-neutral-600 sm:text-lg">
                Turn conversations into saved notes, summaries, and reusable context. Keep useful ideas
                organised, share them easily, and bring them back into future chats when they matter.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-neutral-500">
                Ouro is a general-purpose AI assistant for research, thinking, and everyday idea work.
                Instead of losing useful conversations in a chat thread, you can save what matters,
                keep it organised, and reuse it later.
              </p>

              <JessicaMobileHandoffCard compact className="mt-8" />

              <ul className="mt-8 space-y-5">
                {CAPABILITIES.map(({ icon: Icon, title, body }) => (
                  <li key={title} className="flex gap-3.5 text-left">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#ead6ff] bg-[#faf5ff] text-[#7c3aed]">
                      <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-base font-semibold text-neutral-900">{title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-neutral-600 sm:text-[0.9375rem]">
                        {body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JessicaExampleModal;
